import React, { useRef, useState, useEffect } from "react";
import "../CommunityDetail/CommunityDetail.css";
import UploadFile from "../../../../assets/images/UploadFile.svg";
import editIcon from "../../../../assets/images/edit.svg";
import CopyIcon from "../../../../assets/images/Copy.svg";
import FloatingTextFiled from "../../CommonComponents/FloatingTextFiled/FloatingTextFiled";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { toastify } from "../../../../helpers/toast/toastify";
import {
  setRefetchMultiCommunityList,
  setRefetchSpecificUser,
  setRefetchPage,
} from "../../../../redux/multicommunity/multicommunity";
import BubbleSvgButton from "../../CommonComponents/BubbleSvgButton/BubbleSvgButton";
import axios from "axios";
import Modal from "react-bootstrap/Modal";
import AvatarEditor from "react-avatar-editor";
import { toast } from "react-toastify";
import './traderDetail.css';

const config = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

const TraderDetail = () => {
  const api = useApi();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [originalFile, setOriginalFile] = useState(null); // Store original file
  const [newTradeName, setNewTradeName] = useState("");
  const [originalTradeName, setOriginalTradeName] = useState("");
  const [editableField, setEditableField] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [communityLogo, setCommunityLogo] = useState("");
  const [showCropper, setShowCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [editor, setEditor] = useState(null);
  const [scale, setScale] = useState(1);
  const [showInitialModal, setShowInitialModal] = useState(false);
  const [showEditChoiceModal, setShowEditChoiceModal] = useState(false);
  const [isProgrammaticUpload, setIsProgrammaticUpload] = useState(false);
  
  const selectedCommunity = useSelector(
    (state) => state?.multiCommunity?.currentCommunity
  );
  const { user } = useSelector((state) => state.user);
  const tradeName = user.tradeName || user.mainLeader.tradeName;
  const inputRefs = useRef({});
  const communityOrTraderParams = (
    user?.tradeName || user?.leaderCommunity?.tradeName
  )
    ?.split(" ")
    ?.join("_");

  const publicVerifiedTradesLink = `${document.location.origin.replace(
    document.location.pathname,
    ""
  )}/verifiedTrades/${communityOrTraderParams}`;

  // Handle click outside functionality for tradeName
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editableField === "tradeName") {
        const editableFieldContainer = event.target.closest(".editable-field");
        const saveButtonContainer = event.target.closest(".submitbtn");

        if (!editableFieldContainer && !saveButtonContainer) {
          setNewTradeName(originalTradeName);
          setEditableField(null);
          setShowButton(false);
        }
      }
    };

    if (editableField === "tradeName") {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [editableField, originalTradeName]);

  const handleEditClick = (fieldId) => {
    setEditableField(fieldId);

    setTimeout(() => {
      if (inputRefs.current[fieldId]) inputRefs.current[fieldId].focus();
    }, 100);
  };

  useEffect(() => {
    setNewTradeName(tradeName);
    setOriginalTradeName(tradeName);
  }, [tradeName]);

  const handleSave = async () => {
    try {
      if (!tradeName) {
        toastify("Trade name required");
        return;
      }

      if (newTradeName !== tradeName) {
        if (tradeName?.length > 30) {
          toastify("Trade name must not be greater than 30 characters.");
          return;
        }

        const response = await api(
          "post",
          "authorizeNewonBoard/updateTradeName",
          {
            tradeName: newTradeName,
          }
        );

        if (response?.status) {
          setEditableField(null);
          setOriginalTradeName(newTradeName);
          dispatch(setRefetchSpecificUser(true));
          dispatch(setRefetchPage("Settings"));
          toastify("Trade name is changed", "success");
          setShowButton(false);
        } else {
          setEditableField(null);
          toastify(response?.message, "error");
        }
      } else {
        setEditableField(null);
        setShowButton(false);
        toastify("Please change trade name", "error");
      }
    } catch (error) {
      toastify(error?.message, "error");
      setShowButton(false);
      setEditableField(null);
    }
  };

  // Upload both original and cropped images
  const uploadBothImages = async (originalFile, croppedBlob, fileName) => {
    try {
      // Upload original image
      const originalFormData = new FormData();
      originalFormData.append("file", originalFile);
      
      const originalResponse = await config.post("community/uploadFile", originalFormData);
      const originalUrl = originalResponse?.data?.data;

      if (!originalUrl) {
        toastify("Failed to upload original image", "error");
        return;
      }

      // Upload cropped image
      const croppedFormData = new FormData();
      croppedFormData.append("file", croppedBlob, `cropped_${fileName}`);
      
      const croppedResponse = await config.post("community/uploadFile", croppedFormData);
      const croppedUrl = croppedResponse?.data?.data;

      if (!croppedUrl) {
        toastify("Failed to upload cropped image", "error");
        return;
      }

      // Update with both URLs
      await updateCommunityLogo(croppedUrl, originalUrl, fileName);
      
    } catch (error) {
      console.error("Upload error:", error);
      toastify("Failed to upload images. Please try again.", "error");
    }
  };

  const updateCommunityLogo = async (logoUrl, originalLogoUrl, fileName) => {
    try {
      const response = await api("post", "community/updateCommunityLogo", {
        communityId: selectedCommunity?._id,
        logo: logoUrl,
        originalLogo: originalLogoUrl, // Include original logo URL
      });

      if (response?.status) {
        setCommunityLogo(logoUrl);
        setFileName(fileName);
        toastify("Image Saved", "success");

        // Trigger refetch to update the community data
        dispatch(setRefetchSpecificUser(true));
        dispatch(setRefetchMultiCommunityList(true));
        dispatch(setRefetchPage("Settings"));
      } else {
        toastify("Failed to update community logo. Please try again.", "error");
      }
    } catch (error) {
      console.error("Logo update error:", error);
      toastify("Failed to update community logo. Please try again.", "error");
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!allowedTypes.includes(file.type)) {
        toastify("Please select a valid image file (JPEG, PNG, GIF)", "error");
        event.target.value = "";
        setIsProgrammaticUpload(false);
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toastify("File size must be less than 5MB", "error");
        event.target.value = "";
        setIsProgrammaticUpload(false);
        return;
      }

      // Store the original file
      setOriginalFile(file);
      setFileName(file.name);

      // Read file as data URL for cropper
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    
    // Always reset input
    event.target.value = "";
  };

  const handleCropSave = async () => {
    if (editor && originalFile) {
      const canvas = editor.getImageScaledToCanvas();
      canvas.toBlob(async (croppedBlob) => {
        setShowCropper(false);
        
        // Upload both original and cropped images
        await uploadBothImages(originalFile, croppedBlob, fileName || "image.png");
        
        // Reset states
        setImageSrc(null);
        setOriginalFile(null);
        setIsProgrammaticUpload(false);
      }, "image/png");
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageSrc(null);
    setOriginalFile(null);
    setFileName("");
    setIsProgrammaticUpload(false);
  };

  const handleIconClick = () => {
    if (isProgrammaticUpload) return;
    if (!selectedCommunity?.logo && !user?.image) {
      fileInputRef.current?.click();
    } else {
      setShowEditChoiceModal(true);
    }
  };

  const handleEditChoice = async (choice) => {
    setShowEditChoiceModal(false);
    if (choice === "edit") {
      try {
        // Determine which image URL to load
        let imageUrl;
        if (selectedCommunity?._id) {
          // For communities: try original logo first, fallback to logo
          imageUrl = selectedCommunity?.originalLogo || selectedCommunity?.logo;
        } else {
          // For traders/users: try original image first, fallback to image
          imageUrl = user?.originalImage || user?.image;
        }

        if (!imageUrl) {
          toastify("No image found to edit", "error");
          return;
        }

        // Fetch image from backend to avoid CORS issues
        const response = await config.post(
          "community/getImage",
          { imageUrl: imageUrl },
          {
            responseType: "blob", // Important: tell axios to expect binary data
          }
        );

        // Check if response is successful and is actually an image
        if (response.status === 200 && response.data instanceof Blob) {
          // Check content type to ensure it's an image
          const contentType = response.headers["content-type"] || "";
          let blob;
          if (contentType.startsWith("image/")) {
            // It's an image blob - convert to File object
             blob = response.data;
            const file = new File([blob], "original_image.png", {
              type: blob.type || contentType,
            });

            setOriginalFile(file);

            const reader = new FileReader();
            reader.onload = () => {
              setImageSrc(reader.result);
              setShowCropper(true);
            };
            reader.readAsDataURL(blob);
          } else {
            // Response is not an image - might be an error JSON
            const text = await blob.text();
            try {
              const errorData = JSON.parse(text);
              toastify(
                errorData.message || "Unable to load image for editing",
                "error"
              );
            } catch (e) {
              toastify("Unable to load image for editing", "error");
            }
          }
        } else {
          toastify("Unable to load image for editing", "error");
        }
      } catch (error) {
        console.error("Error loading image:", error);

        // Handle error response
        if (error.response) {
          // If error response is a blob (containing JSON error)
          if (error.response.data instanceof Blob) {
            try {
              const errorText = await error.response.data.text();
              const errorData = JSON.parse(errorText);
              toastify(
                errorData.message || "Unable to load image for editing",
                "error"
              );
            } catch (e) {
              toastify("Unable to load image for editing", "error");
            }
          } else if (error.response.data?.message) {
            toastify(error.response.data.message, "error");
          } else {
            toastify("Unable to load image for editing", "error");
          }
        } else {
          toastify("Unable to load image for editing", "error");
        }
      }
    } else if (choice === "upload") {
      setIsProgrammaticUpload(true);
      fileInputRef.current?.click();
    }
  };
  return (
    <>
      <div className="communityDetailBox">
        <div className="headingBox">
          <h2 className="title">Trader Details</h2>
        </div>
        <div className="InputsRow">
          <div className="editable-field fixedWidth1440-298 editableSettingsFields">
            <FloatingTextFiled
              className={
                editableField !== "tradeName"
                  ? "accdetailinputbox"
                  : "accdetailinputbox-active"
              }
              forLabel="community-name"
              id="community-name"
              label="Trader Name"
              placeholder="Trader Name"
              type="text"
              value={newTradeName}
              ref={(el) => (inputRefs.current["tradeName"] = el)}
              disabled={editableField !== "tradeName"}
              onChange={(e) => {
                setShowButton(true);
                setNewTradeName(e.target.value);
              }}
              focusedAllowed={false}
              maxLength={30}
            />
            <span
              className="edit-icon"
              style={{ cursor: "pointer", marginLeft: "10px", opacity: 1 }}
              onClick={() => handleEditClick("tradeName")}
            >
              <img alt="Edit Icon" src={editIcon} style={{ width: "14px", height: "14px" }} />
            </span>
          </div>
          <div className="editable-field fixedWidth1440-298 editableSettingsFields">
            <FloatingTextFiled
              className="accdetailinputbox pdr20"
              forLabel="community-name"
              id="community-name"
              label="Verified trading URL"
              placeholder="Verified trading URL"
              type="text"
              disabled={true}
              focusedAllowed={false}
              value={publicVerifiedTradesLink}
            />
            <span
              className="edit-icon"
              style={{ cursor: "pointer", marginLeft: "10px", opacity: 1 }}
            >
              <img
                alt="Copy Icon"
                src={CopyIcon}
                onClick={() => {
                  navigator.clipboard
                    .writeText(publicVerifiedTradesLink)
                    .then(() => {
                      const toastId = 123;

                      if (!toast.isActive(toastId)) {
                        toast.success(
                          "Verified trades link copied to clipboard!",
                          {
                            id: toastId,
                            autoClose: 1000,
                            position: "top-center",
                            hideProgressBar: true,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            className: "toast-message",
                          }
                        );
                      }
                    })
                    .catch((error) => {
                      console.error("Clipboard write error:", error);
                      toastify(
                        "Failed to copy verified trades link to clipboard.",
                        "error"
                      );
                    });
                }}
                style={{ width: "14px", height: "14px" }}
              />
            </span>
          </div>

          {!user?.leaderCommunity && (
            <div
              className="editable-field fixedWidth1440-298 uploadPhotoField"
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => {
                setIsProgrammaticUpload(false);
                handleIconClick();
              }}
            >
              <FloatingTextFiled
                className="accdetailinputbox"
                label="Upload Photo"
                value={
                  fileName ||
                  (communityLogo || user?.image
                    ? "Change trader photo"
                    : "Upload trader photo")
                }
                disabled={true}
                focusedAllowed={false}
              />
              <span
                className="edit-icon UploadFileImg"
                style={{ cursor: "pointer", marginLeft: "10px", opacity: 1 }}
              >
                <img alt="Upload Icon" src={UploadFile} style={{ width: "18px", height: "18px" }} />
              </span>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,image/png,image/gif"
                style={{ display: "none" }}
              />
            </div>
          )}
        </div>

        {showButton && (
          <div className="submitbtn ms-lg-auto ms-md-auto">
            <BubbleSvgButton
              onClick={handleSave}
              text={"Save edits"}
              type="button"
              variant="green"
              containerStyling={{
                justifyContent: "center",
                padding: 0,
              }}
              mainDivStyle={{
                width: "215px",
                height: "55px",
              }}
            />
          </div>
        )}
      </div>

      {/* Cropper Modal */}
      <Modal show={showCropper} centered>
        <Modal.Header style={{ justifyContent: "center" }}>
          <Modal.Title className='uploadImgModalTitle'>{`Add profile photo / logo`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {imageSrc && (
            <div style={{ textAlign: "center" }}>
              <AvatarEditor
                ref={setEditor}
                image={imageSrc}
                width={200}
                height={200}
                border={50}
                borderRadius={100}
                color={[255, 255, 255, 0.6]}
                scale={scale}
                rotate={0}
              />
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={scale}
                onChange={(e) => setScale(parseFloat(e.target.value))}
                style={{ width: 200, marginTop: 10 }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "center" }}>
          <BubbleSvgButton
            className="imageCropButtons"
            text="Cancel"
            variant="black"
            onClick={handleCropCancel}
            mainDivStyle={{ width: "120px", height: "40px" }}
            containerStyling={{
              justifyContent: "center",
              padding: 0,
            }}
          />
          <BubbleSvgButton
            className="imageCropButtons"
            text="Save Crop"
            variant="green"
            onClick={handleCropSave}
            mainDivStyle={{ width: "120px", height: "40px" }}
            containerStyling={{
              justifyContent: "center",
              padding: 0,
            }}
          />
        </Modal.Footer>
      </Modal>

      {/* Initial upload modal for first time */}
      <Modal
        show={showInitialModal}
        centered
        onHide={() => setShowInitialModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title className='uploadImgModalTitle'>Upload Community Logo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="uploadImgModalSubTitle" style={{maxWidth: '270px'}}>Please upload your community logo to get started.</p>
          <div style={{ textAlign: "center" }}>
            <BubbleSvgButton
              text="Upload from Computer"
              type="button"
              variant="green"
              onClick={() => {
                setShowInitialModal(false);
                fileInputRef.current?.click();
              }}
              mainDivStyle={{ width: "215px", minHeight: "55px", height: "auto" }}
              containerStyling={{ justifyContent: "center", padding: 0 }}
            />
          </div>
        </Modal.Body>
      </Modal>

      {/* Edit or upload new photo modal */}
      <Modal
        show={showEditChoiceModal}
        centered
        onHide={() => setShowEditChoiceModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title className='uploadImgModalTitle'>Edit Community Logo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="uploadImgModalSubTitle">Would you like to edit your existing photo or upload a new one?</p>
          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <BubbleSvgButton
              text="Edit Existing Photo"
              type="button"
              variant="black"
              onClick={() => handleEditChoice("edit")}
              mainDivStyle={{ width: "160px", minHeight: '45px', height: "auto" }}
              containerStyling={{ justifyContent: "center", padding: 0 }}
            />
            <BubbleSvgButton
              text="Upload New Photo"
              type="button"
              variant="green"
              onClick={() => {
                setIsProgrammaticUpload(true);
                fileInputRef.current?.click();
                setShowEditChoiceModal(false);
              }}
              mainDivStyle={{ width: "160px", minHeight: '45px', height: "auto" }}
              containerStyling={{ justifyContent: "center", padding: 0 }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TraderDetail;